import styled, { css } from 'styled-components';

import { Options } from 'containers/SharedList/AccessDropdown/styles';

import { Colors } from 'components/utils/styles/ui';
import { richContentStyles } from 'components/utils/styles/richContent';
import { Button as ButtonBase } from 'components/elements/button';
import { Icon as IconBase } from 'components/elements/icon';
import { iconElementsSelector } from 'components/elements/styles/icon';
import { TagCompact } from 'components/navigation/menu/styles/link';
import { styledScrollbarMixin } from 'components/utils/styles/scrollbar';

export const tooltipShadow = css`
  background-color: ${Colors.white};
  border-radius: 0.4rem;
  filter: drop-shadow(0px 0px 16px rgba(19, 41, 61, 0.08))
    drop-shadow(0px 6px 16px rgba(19, 41, 61, 0.12));
`;

export const bubbleMarksStyles = css`
  .tooltip-shadow {
    ${tooltipShadow}
  }

  .tippy-svg-arrow {
    pointer-events: none;
    svg {
      fill: ${Colors.white};
    }
  }
`;

export const ShowMoreLessWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 2.6rem;
`;

export const marginMixin = css`
  margin-top: 0;
  margin-bottom: 0.8rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const resizableMixin = ({ resizable }: { resizable: boolean }) =>
  resizable &&
  css`
    resize: vertical;
  `;

export const Container = styled.div``;

export const AskAIAnchor = styled.div`
  flex: none;
`;

type WrapperProps = {
  hasFocus: boolean;
  isValid: boolean;
  withOverflow: boolean;
  editable: boolean;
  height: number;
  maxHeight: number;
  minHeight: number;
  resizable: boolean;
  richContent?: boolean;
};

export const Wrapper = styled.div<WrapperProps>`
  border: 1px solid
    ${({ hasFocus, isValid }) => {
      if (hasFocus || (!isValid && hasFocus)) {
        return Colors.teal;
      }
      if (!isValid) {
        return Colors.red;
      }
      return Colors.inputOutline;
    }};
  position: relative;
  border-radius: 0.4rem;

  ${({ editable }) =>
    !editable &&
    css`
      pointer-events: none;
      cursor: not-allowed;
      background-color: ${Colors.greyLighter};
      .ProseMirror {
        background-color: transparent;
      }
    `}

  .ProseMirror {
    word-break: break-word;
    outline: none;
    padding-right: 1.2rem;
    overflow: ${({ withOverflow }) => (withOverflow ? 'auto' : 'hidden')};
    padding: 1.4rem 0
      ${({ withOverflow, editable }) =>
        withOverflow || editable ? '1.4rem' : 0}
      1.2rem;

    ${({ height }) => height && `height: ${height};`}
    ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight};`}
    ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}

    table {
      border-collapse: collapse;
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100%;
      table-layout: fixed;
      overflow: hidden;

      th {
        background-color: ${Colors.tealLightest};
      }

      td,
      th {
        border: 1px solid ${Colors.outline};
        padding: 0.4rem 0.8rem;
        text-align: left;
        vertical-align: middle;
        position: relative;
        overflow: hidden;
        word-break: break-word;

        &.selectedCell {
          background-color: ${Colors.greyLight};
        }
      }
    }

    .tableWrapper {
      contain: inline-size;
      overflow: hidden;
      margin: 0.8rem 0;
      max-width: 100%;
      width: 100%;
      padding-right: 1.2rem;
    }

    .column-resize-handle {
      position: absolute;
      right: -0.2rem;
      top: 0;
      bottom: -0.2rem;
      width: 0.4rem;
      background-color: ${Colors.teal};
      pointer-events: none;
    }

    &.resize-cursor {
      cursor: col-resize;
    }

    p {
      line-height: 2rem;
      vertical-align: middle;

      &.is-editor-empty:first-child::before {
        color: ${Colors.textLighter};
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
      }
    }

    ul,
    ol {
      ${marginMixin};
    }

    ${styledScrollbarMixin}
    ${resizableMixin};

    ${({ richContent }) =>
      richContent
        ? richContentStyles
        : css`
            p {
              margin: 0;
            }

            ul {
              list-style: unset;
              padding-left: 1.6rem;
            }
          `}
  }

  ${bubbleMarksStyles}

  .variable,
  .node-variable {
    display: inline-block;
    height: 2.7rem;

    ${({ editable }) => !editable && 'pointer-events: none;'}
  }
`;

export const ShowMoreLessButton = styled(ButtonBase)`
  text-decoration: underline;
  padding: 0.4rem 1.4rem 1.2rem 1.4rem;
`;

export const MarkButton = styled(ButtonBase).attrs({
  type: 'blank',
})<{ active: boolean }>`
  margin: auto;
  position: relative;
  height: 3.6rem;
  width: 4rem;

  ${iconElementsSelector} {
    stroke: ${({ active }) =>
      active ? Colors.tealDark : Colors.textLight} !important;
  }

  &:hover {
    background-color: ${Colors.tealLightest};
  }
`;

export const VariablesButton = styled(MarkButton)`
  height: 3rem;
  width: 3rem;
`;

const noRightBordersMixin = css`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  ${iconElementsSelector} {
    stroke: ${Colors.grape} !important;
  }
`;

const nonFloatingButtonMixin = ({
  withBarTextTools,
}: {
  withBarTextTools: boolean;
}) =>
  withBarTextTools
    ? css`
        height: 2.4rem;
        flex: none;
      `
    : css`
        height: 2.4rem;
        position: absolute;
        right: 0;
        top: -3rem;
      `;

export const AskButton = styled(ButtonBase)<{
  floating: boolean;
  withBarTextTools: boolean;
}>`
  ${({ floating }) => (floating ? noRightBordersMixin : nonFloatingButtonMixin)}
`;

export const AskAIText = styled.div<{ floating: boolean }>`
  ${({ floating }) => floating && `color: ${Colors.grape};`}
  font-weight: 500;
  font-size: 1.4rem;
  margin-left: 0.6rem;
`;

export const Icon = styled(IconBase).attrs({
  size: 'normal',
})``;

export const VariablesList = styled(Options)`
  display: grid;
  grid-auto-rows: 3.6rem;
  align-items: center;
  min-width: 24.2rem;
  right: 3.5rem;
  box-shadow: none;
`;

export const VariablesListTitle = styled.div`
  font-weight: 500;
  padding: 0 1.2rem;
  color: ${Colors.textLighter};
  border-top: 1px solid ${Colors.outline};

  ${({ theme }) => theme.typography.text.small}
  line-height: 3rem;

  text-transform: uppercase;
  &:first-child {
    border-top: none;
  }
`;

export const VariablesListItemHeight = 3.6;

export const VariablesListItem = styled.div`
  font-weight: 500;
  line-height: ${VariablesListItemHeight}rem;
  color: ${Colors.text};
  padding: 0 1.2rem;
  cursor: pointer;

  ${TagCompact} {
    font-weight: 500;
  }

  &:hover {
    background-color: ${Colors.tealLightest};
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin: 1.2rem 0;

  ${MarkButton} {
    border: 1px solid ${Colors.outline};
    flex: none;
    margin: 0;
  }
`;

export const ToolbarRow = styled.div<{ sticky?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  ${({ sticky }) =>
    sticky &&
    css`
      padding: 0.6rem 0;
      margin: 0.6rem 0;

      ${Container}:focus-within & {
        position: sticky;
        top: 5.4rem;
        z-index: 2400;
        background-color: ${Colors.white};
      }
    `}
`;

export const DropdownButton = styled(MarkButton)`
  width: auto;
  min-width: 4rem;
  padding: 0 0.4rem 0 0.8rem;
`;

export const CaretIcon = styled(IconBase).attrs({
  size: 0.8,
})`
  && {
    width: 0.8rem;
    height: 0.8rem;
  }
`;

export const CharCount = styled.div`
  margin-top: 1.2rem;
  color: ${Colors.textLight};
  ${({ theme }) => theme.typography.text.normal}
`;

export const TableToolButton = styled(MarkButton)<{ danger?: boolean }>`
  height: 3rem;
  width: 3.2rem;

  &:hover {
    background-color: ${({ danger }) =>
      danger ? Colors.redLight : Colors.tealLightest};

    ${iconElementsSelector} {
      stroke: ${({ danger }) =>
        danger ? Colors.red : Colors.tealDark} !important;
    }
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;

      ${iconElementsSelector} {
        stroke: ${Colors.textLight} !important;
      }
    }
  }
`;

export const TableToolsSeparator = styled.div`
  width: 1px;
  height: 2rem;
  background-color: ${Colors.outline};
  margin: auto 0.2rem;
`;

export const TableToolsWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.2rem;
`;

export const TableActionGroup = styled.span`
  display: flex;
  align-items: center;
`;

export const TableToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 3rem;
  padding: 0 0.4rem;

  && label {
    display: block;
    margin-bottom: 0;
  }
`;

export const DropdownWrapper = styled.div`
  position: relative;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  z-index: 10;
  min-width: 16rem;
  padding: 0.4rem;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.outline};
  border-radius: 0.4rem;
`;

export const DropdownItem = styled.button.attrs({ type: 'button' })<{
  selected: boolean;
}>`
  display: block;
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: none;
  border-radius: 0.4rem;
  text-align: left;
  cursor: pointer;
  background-color: ${({ selected }) =>
    selected ? Colors.tealLightest : Colors.white};
  color: ${({ selected }) => (selected ? Colors.tealDark : Colors.text)};

  &:hover {
    background-color: ${Colors.tealLightest};
  }
`;

export const LinkPopover = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.outline};
  border-radius: 0.4rem;
`;

export const LinkForm = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem;
`;

export const LinkInput = styled.input<{ invalid?: boolean }>`
  width: 24rem;
  height: 3rem;
  padding: 0 0.8rem;
  border: 1px solid ${({ invalid }) => (invalid ? Colors.red : Colors.outline)};
  border-radius: 0.4rem;
  color: ${Colors.text};

  &:focus {
    outline: none;
    border-color: ${({ invalid }) => (invalid ? Colors.red : Colors.teal)};
  }
`;

export const FloatingTools = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
`;
